import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { AiOutlineReload } from 'react-icons/ai';
import { FiActivity, FiAlertTriangle, FiCheckCircle, FiServer } from 'react-icons/fi';
import ContentWrapper from '../components/contentwrapper/ContentWrapper';
import { fetchCallAllDataByIDAPI } from '../utils/api';

const getServerAccent = (type) => {
    const key = String(type || '').toLowerCase();
    if (key === 'convox') return 'orange';
    if (key === 'verb') return 'purple';
    return 'blue';
};

const ServerHealth = () => {
    const [health, setHealth] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchServerHealth = (silent = false) => {
        setIsLoading(true);
        fetchCallAllDataByIDAPI('ppc-server-health').then((res) => {
            setIsLoading(false);
            if (res?.data?.status === 1) {
                setHealth(res.data.data);
                if (!silent) toast.success('Server health refreshed');
            } else {
                toast.error('Unable to load server health');
            }
        }).catch(() => {
            setIsLoading(false);
            toast.error('Connection failed');
        });
    };

    useEffect(() => {
        fetchServerHealth(true);
    }, []);

    const servers = useMemo(() => health?.servers || [], [health]);
    const total = Number(health?.total || servers.length || 0);
    const healthy = Number(health?.healthy || 0);
    const issues = Number(health?.issues || Math.max(total - healthy, 0));
    const healthPercent = total > 0 ? Math.round((healthy / total) * 100) : 0;
    const lastChecked = health?.checked_at || 'Not checked yet';

    return (
        <>
            <Helmet>
                <title>PPC Server Health</title>
            </Helmet>
            <ContentWrapper>
                <div className="misDashboard server-health-page">
                    <div className="server-health-content">
                        <section className="server-health-hero">
                            <div className="server-health-copy">
                                <span className="server-health-eyebrow">PPC INFRA MONITOR</span>
                                <h1>Server Health</h1>
                                <p>Realtime check for 4 NGUCC, 2 Convox and 1 Verb call servers.</p>
                            </div>
                            <div className="server-health-actions">
                                <div className="server-health-score">
                                    <span>{healthPercent}%</span>
                                    <small>Healthy</small>
                                </div>
                                <button type="button" onClick={() => fetchServerHealth()} disabled={isLoading}>
                                    <AiOutlineReload className={isLoading ? 'is-spinning' : ''} />
                                    {isLoading ? 'Checking...' : 'Refresh'}
                                </button>
                            </div>
                        </section>

                        <section className="server-health-summary">
                            <div className="server-health-stat">
                                <FiServer />
                                <span>Total Servers</span>
                                <strong>{total}</strong>
                            </div>
                            <div className="server-health-stat is-ok">
                                <FiCheckCircle />
                                <span>Connected</span>
                                <strong>{healthy}</strong>
                            </div>
                            <div className="server-health-stat is-issue">
                                <FiAlertTriangle />
                                <span>Issue</span>
                                <strong>{issues}</strong>
                            </div>
                            <div className="server-health-stat">
                                <FiActivity />
                                <span>Last Checked</span>
                                <strong>{lastChecked}</strong>
                            </div>
                        </section>

                        <section className="server-health-grid">
                            {isLoading && servers.length === 0 ? (
                                Array.from({ length: 7 }).map((_, index) => (
                                    <div className="server-node-card is-skeleton" key={index}>
                                        <span />
                                        <strong />
                                        <p />
                                        <em />
                                    </div>
                                ))
                            ) : (
                                servers.map((server) => {
                                    const isOk = server.status === 'ok' || server.ok === true;
                                    const accent = getServerAccent(server.type);

                                    return (
                                        <article className={`server-node-card ${isOk ? 'is-ok' : 'is-down'} accent-${accent}`} key={server.key || server.name || server.label}>
                                            <div className="server-node-top">
                                                <div>
                                                    <span className="server-node-type">{server.type || 'server'}</span>
                                                    <h3>{server.label}</h3>
                                                </div>
                                                <span className="server-node-state">
                                                    {isOk ? <FiCheckCircle /> : <FiAlertTriangle />}
                                                    {isOk ? 'OK' : 'Issue'}
                                                </span>
                                            </div>
                                            <div className="server-node-meta">
                                                <span>Response Time</span>
                                                <span>{server.response_ms ? `${server.response_ms} ms` : '-- ms'}</span>
                                            </div>
                                            <p>{server.message || (isOk ? 'Connection healthy' : 'Connection issue detected')}</p>
                                        </article>
                                    );
                                })
                            )}
                        </section>
                    </div>
                </div>
            </ContentWrapper>
        </>
    );
};

export default ServerHealth;
